import { Pressable, View } from "@-/com.npmjs.react-native";
import { Image as ImageFromExpoImage } from "@-/com.npmjs.expo-image";
import { withUniwind } from "@-/com.npmjs.uniwind";
import BOOTSPLASH_HANI from "@-/dev.improve.haniverse.assets.logo/@images/bootsplash-logo.png";
import { useHanCoinDepositSource } from "@-/dev.improve.haniverse.components.ui.han-coin-deposit";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { describeHanReward } from "@-/dev.improve.haniverse.lib.han-currency";
import { useIsActorSyncPreparing } from "@-/dev.improve.haniverse.routes.nav._context";
import {
  useLearnMapModeSelector,
  useLeaveLearnMap,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.map-mode";
import { useContext, useEffect, useState } from "react";
import { ChromeReadyMarker } from "@-/dev.improve.haniverse.components.ui.chrome-ready-marker";
import { LearnSummaryDailyReward, LearnSummaryDailyRewardBadge } from "#learn-summary-daily-reward";
import { useLearnSummaryDailyReward } from "#use-learn-summary-daily-reward";
import { useWelcomeHaniGreeting } from "#use-welcome-hani-greeting";
import { LearnSummaryMapWelcomePresentationContext } from "#learn-summary-map-welcome-presentation-context";

const Image = withUniwind(ImageFromExpoImage);

/**
 * The map's first frame. Its greeting and action wait on no database or network state; the cards
 * own loading after Continue Learning is pressed. Due reviews are not listed here: the Learn
 * summary card shows them. The week's 红包 joins once it loads, and today's unopened one joins
 * Continue Learning too: pressing the button opens it, its coins bursting out of the button.
 */
export function LearnSummaryMapWelcome() {
  const presentation = useContext(LearnSummaryMapWelcomePresentationContext);
  const continueGreeting = useWelcomeHaniGreeting();
  const leaveMap = useLeaveLearnMap();
  const isMapShown = useLearnMapModeSelector((mode) => mode.isMapShown) ?? true;
  const [hasLayout, setHasLayout] = useState(false);
  const isSyncPreparing = useIsActorSyncPreparing();
  // Continue Learning alone collects; the strip only displays the shared reward state.
  const dailyReward = useLearnSummaryDailyReward({
    enabled: !isSyncPreparing,
    source: "continue-learning",
  });
  const offeredReward = dailyReward.gate?.kind === "offer" ? dailyReward.gate.reward : null;
  const offeredDay =
    dailyReward.gate?.kind === "offer"
      ? dailyReward.gate.week.find((day) => day.status === "today")?.dayKey
      : undefined;
  useEffect(() => {
    if (offeredDay !== undefined && isMapShown) {
      presentation?.offerDailyReward(offeredDay);
    }
  }, [isMapShown, offeredDay, presentation]);
  const continueSourceRef = useHanCoinDepositSource("continue-learning");
  return (
    <View
      className="gap-3 px-[9px] pt-3 pb-[9px]"
      onLayout={({ nativeEvent: { layout } }) => {
        if (layout.height > 0) {
          setHasLayout(true);
        }
      }}
      testID="learn-map-welcome"
    >
      {isMapShown && hasLayout ? <ChromeReadyMarker /> : null}
      {presentation?.isRewardPopover ? (
        <Image className="mx-auto size-24" contentFit="contain" source={BOOTSPLASH_HANI} />
      ) : null}
      <Text className="text-center text-2xl font-semibold text-foreground">Welcome Back</Text>
      {dailyReward.gate === null ? null : <LearnSummaryDailyReward gate={dailyReward.gate} />}
      <Pressable
        accessibilityHint={
          offeredReward === null
            ? undefined
            : `Also opens today's red envelope: ${describeHanReward(offeredReward)}`
        }
        accessibilityLabel="Continue Learning"
        accessibilityRole="button"
        className="h-12 flex-row items-center justify-center gap-2 rounded-full bg-foreground px-6 active:opacity-80"
        collapsable={false}
        onPress={() => {
          if (continueGreeting?.() === false) {
            return;
          }
          dailyReward.claim();
          leaveMap();
        }}
        ref={continueSourceRef}
        testID="learn-map-welcome-start"
      >
        <Text className="text-base font-semibold text-background" numberOfLines={1}>
          Continue Learning
        </Text>
        {offeredReward === null ? null : <LearnSummaryDailyRewardBadge reward={offeredReward} />}
      </Pressable>
    </View>
  );
}
